/**
 * InvoicesTable — Platform-wide invoice management table
 *
 * Self-contained: reads directly from useBillingStore.
 * Features: debounced search, status/date filters, pagination,
 * per-row account navigation, loading skeleton, error + empty states.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from '@respectly/ui';
import { cn } from '@respectly/ui';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Receipt,
  RefreshCw,
  RotateCcw,
  Scale,
  Search,
  SlidersHorizontal,
  XCircle,
  X,
} from 'lucide-react';
import { useBillingStore, useActiveInvoiceFiltersCount } from '../../stores/useBillingStore';
import type { InvoiceStatus } from '@respectly/shared-types';

// ============================================
// Formatters
// ============================================

function formatCurrency(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(date: Date | string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date));
}

function formatPeriod(start: Date | string, end: Date | string) {
  const fmt = (d: Date | string) =>
    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(d));
  return `${fmt(start)} – ${fmt(end)}, ${new Date(end).getFullYear()}`;
}

// ============================================
// Status badge config
// ============================================

const statusConfig: Record<
  InvoiceStatus,
  { label: string; className: string; dot: string; icon: React.ReactNode }
> = {
  paid: {
    label: 'Paid',
    dot: 'bg-emerald-500',
    className:
      'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:ring-emerald-800/60',
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  pending: {
    label: 'Pending',
    dot: 'bg-amber-500',
    className:
      'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:ring-amber-800/60',
    icon: <Clock className="h-3 w-3" />,
  },
  overdue: {
    label: 'Overdue',
    dot: 'bg-red-500',
    className:
      'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-400 dark:ring-red-800/60',
    icon: <AlertTriangle className="h-3 w-3" />,
  },
  draft: {
    label: 'Draft',
    dot: 'bg-slate-400',
    className:
      'bg-slate-50 text-slate-600 ring-1 ring-inset ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700',
    icon: <FileText className="h-3 w-3" />,
  },
  cancelled: {
    label: 'Cancelled',
    dot: 'bg-slate-400',
    className:
      'bg-slate-50 text-slate-500 ring-1 ring-inset ring-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:ring-slate-700',
    icon: <XCircle className="h-3 w-3" />,
  },
  refunded: {
    label: 'Refunded',
    dot: 'bg-sky-500',
    className:
      'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:ring-sky-800/60',
    icon: <RotateCcw className="h-3 w-3" />,
  },
  disputed: {
    label: 'Disputed',
    dot: 'bg-orange-500',
    className:
      'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200 dark:bg-orange-950/40 dark:text-orange-400 dark:ring-orange-800/60',
    icon: <Scale className="h-3 w-3" />,
  },
};

// ============================================
// Skeleton
// ============================================

export function InvoicesTableSkeleton() {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 dark:border-slate-800">
            {['Invoice', 'Account', 'Period', 'Status', 'Subtotal', 'Tax', 'Total', 'Due Date', ''].map(
              (col, i) => (
                <th
                  key={i}
                  className={cn(
                    'py-2.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500',
                    i === 0 ? 'px-5 text-left' : i >= 4 ? 'px-4 text-right' : 'px-4 text-left'
                  )}
                >
                  {col}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
          {Array.from({ length: 8 }).map((_, i) => (
            <tr key={i} className="animate-pulse">
              <td className="px-5 py-3.5">
                <Skeleton className="h-3.5 w-24 mb-1" />
                <Skeleton className="h-3 w-16" />
              </td>
              <td className="px-4 py-3.5">
                <Skeleton className="h-3.5 w-32 mb-1" />
                <Skeleton className="h-3 w-20" />
              </td>
              <td className="px-4 py-3.5">
                <Skeleton className="h-3.5 w-36" />
              </td>
              <td className="px-4 py-3.5">
                <Skeleton className="h-5 w-16 rounded-full" />
              </td>
              <td className="px-4 py-3.5 text-right">
                <Skeleton className="h-3.5 w-14 ml-auto" />
              </td>
              <td className="px-4 py-3.5 text-right">
                <Skeleton className="h-3.5 w-10 ml-auto" />
              </td>
              <td className="px-4 py-3.5 text-right">
                <Skeleton className="h-3.5 w-14 ml-auto" />
              </td>
              <td className="px-4 py-3.5 text-right">
                <Skeleton className="h-3.5 w-20 ml-auto" />
              </td>
              <td className="px-5 py-3.5 text-right">
                <Skeleton className="h-6 w-10 ml-auto" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Component
// ============================================

export function InvoicesTable({ className }: { className?: string }) {
  const navigate = useNavigate();
  const activeFiltersCount = useActiveInvoiceFiltersCount();

  const {
    invoices,
    invoicesPagination,
    invoiceFilters,
    isLoadingInvoices,
    invoicesError,
    setInvoiceFilter,
    clearInvoiceFilters,
    loadInvoices,
  } = useBillingStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load on mount, clear filters on unmount
  useEffect(() => {
    loadInvoices(1);
    return () => {
      clearInvoiceFilters();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search → store filter
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setInvoiceFilter('search', searchValue.trim() || undefined);
      setCurrentPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusFilter = (value: string) => {
    setCurrentPage(1);
    setInvoiceFilter('status', value === 'all' ? undefined : (value as InvoiceStatus));
  };

  const handleDateFilter = (value: string) => {
    setCurrentPage(1);
    setInvoiceFilter('dateRange', value === 'all' ? undefined : (value as 'all' | '7days' | '30days' | '90days'));
  };

  const handleClearFilters = () => {
    setSearchValue('');
    clearInvoiceFilters();
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadInvoices(page);
  };

  const handleViewAccount = (tenantId: string) => {
    navigate({ to: '/accounts/$id', params: { id: tenantId } });
  };

  const totalPages = invoicesPagination?.totalPages ?? 1;

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60 shadow-sm', className)}>
      {/* ── Toolbar ──────────────────────────────────── */}
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 px-5 pb-3 pt-4">
        {/* Left: title + total */}
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800">
            <Receipt className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800 dark:text-white leading-none">
              All Invoices
            </p>
            {invoicesPagination && (
              <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
                {invoicesPagination.total.toLocaleString()} total
              </p>
            )}
          </div>
        </div>

        {/* Right: filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search invoice, account…"
              className="h-8 pl-8 pr-3 w-52 text-xs border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 placeholder:text-slate-400"
            />
            {searchValue && (
              <button
                onClick={() => setSearchValue('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <Select value={invoiceFilters.status ?? 'all'} onValueChange={handleStatusFilter}>
            <SelectTrigger className="h-8 w-[132px] text-xs border-slate-200 dark:border-slate-700">
              <SlidersHorizontal className="mr-1.5 h-3 w-3 text-slate-400" />
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="disputed">Disputed</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          {/* Date range filter */}
          <Select
            value={invoiceFilters.dateRange ?? 'all'}
            onValueChange={handleDateFilter}
          >
            <SelectTrigger className="h-8 w-[120px] text-xs border-slate-200 dark:border-slate-700">
              <SelectValue placeholder="All time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="7days">Last 7 days</SelectItem>
              <SelectItem value="30days">Last 30 days</SelectItem>
              <SelectItem value="90days">Last 90 days</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear filters */}
          <AnimatePresence>
            {activeFiltersCount > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="h-8 gap-1.5 px-2.5 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  <X className="h-3 w-3" />
                  Clear
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700 text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                    {activeFiltersCount}
                  </span>
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Refresh */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 border-slate-200 dark:border-slate-700"
            onClick={() => loadInvoices(currentPage)}
            disabled={isLoadingInvoices}
            title="Refresh"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5 text-slate-500', isLoadingInvoices && 'animate-spin')}
            />
          </Button>
        </div>
      </CardHeader>

      {/* ── Body ─────────────────────────────────────── */}
      <CardContent className="p-0">
        {/* Error state */}
        {invoicesError && !isLoadingInvoices && (
          <div className="flex flex-col items-center gap-3 py-14 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-50 dark:bg-red-950/40">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Failed to load invoices
              </p>
              <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
                {invoicesError.message}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => loadInvoices(currentPage)}
            >
              <RefreshCw className="mr-1.5 h-3 w-3" />
              Retry
            </Button>
          </div>
        )}

        {/* Loading */}
        {isLoadingInvoices && <InvoicesTableSkeleton />}

        {/* Table or empty */}
        {!isLoadingInvoices && !invoicesError && (
          <>
            {invoices.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800">
                  <Receipt className="h-6 w-6 text-slate-400 dark:text-slate-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white">
                    No invoices found
                  </p>
                  <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                    {activeFiltersCount > 0
                      ? 'No invoices match your current filters.'
                      : 'No invoices have been generated yet.'}
                  </p>
                </div>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleClearFilters}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-800/80">
                      <th className="px-5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                        Invoice
                      </th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                        Account
                      </th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                        Period
                      </th>
                      <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                        Status
                      </th>
                      <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                        Subtotal
                      </th>
                      <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                        Tax
                      </th>
                      <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                        Total
                      </th>
                      <th className="px-4 py-2.5 text-right text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                        Due Date
                      </th>
                      <th className="px-5 py-2.5 text-right" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                    {invoices.map((invoice, i) => {
                      const sc = statusConfig[invoice.status as InvoiceStatus];
                      const isOverdue = invoice.status === 'overdue';
                      return (
                        <motion.tr
                          key={invoice.id}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.15, delay: i * 0.025 }}
                          className={cn(
                            'group transition-colors',
                            isOverdue
                              ? 'bg-red-50/20 dark:bg-red-950/5 hover:bg-red-50/50 dark:hover:bg-red-950/10'
                              : 'hover:bg-slate-50/80 dark:hover:bg-slate-800/20'
                          )}
                        >
                          {/* Invoice # */}
                          <td className="px-5 py-3.5">
                            <p className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-200 tracking-tight">
                              {invoice.invoiceNumber}
                            </p>
                            <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-600">
                              {formatDate(invoice.createdAt)}
                            </p>
                          </td>

                          {/* Account — clickable */}
                          <td className="px-4 py-3.5">
                            <button
                              onClick={() => handleViewAccount(invoice.tenantId)}
                              className="group/acct flex items-center gap-1.5 text-left"
                            >
                              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800 group-hover/acct:bg-indigo-50 dark:group-hover/acct:bg-indigo-950/40 transition-colors">
                                <Building2 className="h-3 w-3 text-slate-500 dark:text-slate-400 group-hover/acct:text-indigo-600 dark:group-hover/acct:text-indigo-400 transition-colors" />
                              </div>
                              <span className="text-xs font-medium text-slate-700 dark:text-slate-300 group-hover/acct:text-indigo-600 dark:group-hover/acct:text-indigo-400 transition-colors">
                                {invoice.tenantName ?? invoice.tenant?.name ?? invoice.tenantId}
                              </span>
                            </button>
                          </td>

                          {/* Period */}
                          <td className="px-4 py-3.5 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                            {formatPeriod(invoice.periodStart, invoice.periodEnd)}
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3.5">
                            {sc ? (
                              <span
                                className={cn(
                                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
                                  sc.className
                                )}
                              >
                                {sc.icon}
                                {sc.label}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">{invoice.status}</span>
                            )}
                          </td>

                          {/* Subtotal */}
                          <td className="px-4 py-3.5 text-right text-xs tabular-nums text-slate-600 dark:text-slate-400">
                            {formatCurrency(invoice.subtotal, invoice.currency)}
                          </td>

                          {/* Tax */}
                          <td className="px-4 py-3.5 text-right text-xs tabular-nums text-slate-400 dark:text-slate-600">
                            {formatCurrency(invoice.tax, invoice.currency)}
                          </td>

                          {/* Total */}
                          <td className="px-4 py-3.5 text-right text-xs tabular-nums font-semibold text-slate-900 dark:text-white">
                            {formatCurrency(invoice.total, invoice.currency)}
                          </td>

                          {/* Due Date */}
                          <td
                            className={cn(
                              'px-4 py-3.5 text-right text-xs tabular-nums whitespace-nowrap',
                              isOverdue
                                ? 'font-semibold text-red-600 dark:text-red-400'
                                : 'text-slate-500 dark:text-slate-400'
                            )}
                          >
                            {isOverdue && (
                              <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-red-500 align-middle" />
                            )}
                            {formatDate(invoice.dueDate)}
                          </td>

                          {/* Actions */}
                          <td className="px-5 py-3.5 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewAccount(invoice.tenantId)}
                              className="h-6 px-2 text-[11px] text-slate-400 opacity-0 transition-all group-hover:opacity-100 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-700"
                            >
                              View
                            </Button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 px-5 py-3">
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Page {currentPage} of {totalPages}
                  {invoicesPagination?.total != null && (
                    <> &middot; {invoicesPagination.total.toLocaleString()} invoices</>
                  )}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0 border-slate-200 dark:border-slate-700"
                    disabled={currentPage <= 1 || isLoadingInvoices}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <span className="flex h-7 min-w-7 items-center justify-center rounded-md bg-slate-900 dark:bg-white px-2 text-xs font-semibold text-white dark:text-slate-900 tabular-nums">
                    {currentPage}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0 border-slate-200 dark:border-slate-700"
                    disabled={currentPage >= totalPages || isLoadingInvoices}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
